
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import Button from "../../../../../components/Button";
import OverlayModal from "../../../../../components/Modal";
import styles from "../../css/CODTransactions/CODPayment.module.css";
import { LuCreditCard, LuBanknote, LuCircleCheckBig, LuArrowLeft, LuCircleX } from "react-icons/lu";
import { getCodOrderDetails, markCodOrderPaid } from "../../../../../api/frontShop/CODDebtorsAPI.js";

function formatCurrency(amount) {
  const n = Number(amount || 0);
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CODPayment() {
  const location = useLocation();
  const navigate = useNavigate();

  const orderid = location.state?.orderid || null;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const [method, setMethod] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [incompleteOpen, setIncompleteOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderid) return;
    let alive = true;
    setLoading(true);
    setError("");
    setErrorOpen(false);
    getCodOrderDetails(orderid)
      .then((resp) => {
        if (!alive) return;
        setOrder(resp?.data?.data || null);
      })
      .catch((e) => {
        if (!alive) return;
        setOrder(null);
        setError("");
        setErrorTitle("Error");
        setErrorMessage(e?.response?.data?.message || e?.message || "Failed to load order");
        setErrorOpen(true);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [orderid]);

  const safeOrder = useMemo(() => {
    if (order) return order;
    return {
      name: "",
      address: "",
      preferreddate: "",
      totalDue: 0,
      items: [],
    };
  }, [order]);

  const totalDue = useMemo(() => {
    if (typeof safeOrder.totalDue === "number") return safeOrder.totalDue;
    const items = Array.isArray(safeOrder.items) ? safeOrder.items : [];
    return items.reduce((s, it) => s + Number(it.amount || 0), 0);
  }, [safeOrder]);

  const items = Array.isArray(safeOrder.items) ? safeOrder.items : [];

  return (
    <div className={styles.wrapper}>
      <Navbar backTo="/frontshop/debtors/cod-accounts" />

      <div className={styles.container}>
        {loading ? <div className={styles.card}>Loading...</div> : null}

        <div className={styles.card}>
          <div className={styles.customerName}>{safeOrder.name}</div>
          <div className={styles.customerAddress}>{safeOrder.address}</div>
          <div className={styles.customerDelivery}>Delivery: {safeOrder.preferreddate}</div>
        </div>

        <div className={styles.card}>
          <div className={styles.sectionTitle}>Items</div>
          <div className={styles.itemsList}>
            {items.map((it, idx) => (
              <div key={idx} className={styles.itemRow}>
                <div className={styles.itemName}>{`${it.name} × ${it.quantity}`}</div>
                <div className={styles.itemAmount}>{formatCurrency(it.amount)}</div>
              </div>
            ))}
          </div>
          <div className={styles.totalRow}>
            <div className={styles.totalLabel}>Total Due</div>
            <div className={styles.totalAmount}>{formatCurrency(totalDue)}</div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.sectionTitle}>Payment</div>
          <div className={styles.methodRow}>
            <button
              className={`${styles.methodBtn} ${method === "Cash" ? styles.methodActive : ""}`}
              onClick={() => setMethod("Cash")}
              type="button"
            >
              <LuBanknote className={styles.methodIcon} aria-hidden="true" />
              <div className={styles.methodLabel}>Cash</div>
            </button>
            <button
              className={`${styles.methodBtn} ${method === "Card" ? styles.methodActive : ""}`}
              onClick={() => setMethod("Card")}
              type="button"
            >
              <LuCreditCard className={styles.methodIcon} aria-hidden="true" />
              <div className={styles.methodLabel}>Card</div>
            </button>
          </div>

          <div className={styles.amountGiven}>
            <div className={styles.amountLabel}>Amount Given</div>
            <div className={styles.amountValue}>{formatCurrency(totalDue)}</div>
          </div>

          <Button
            variant="approve"
            className={styles.markPaidBtn}
            disabled={!method}
            onClick={async () => {
              try {
                if (!orderid) throw new Error("Missing orderid");
                setError("");
                setIncompleteOpen(false);
                setErrorOpen(false);
                await markCodOrderPaid(orderid, method === "Cash" ? "CASH" : "CARD");
                setReceiptNo(String(safeOrder?.recieptno || ""));
                setSuccessOpen(true);
              } catch (e) {
                const status = e?.response?.status;
                const errTitle = e?.response?.data?.error;
                const errMsg = e?.response?.data?.message;
                const normalizedTitle = String(errTitle || "").trim();
                const normalizedMsg = String(errMsg || "").trim();
                if (
                  status === 409 ||
                  normalizedTitle === "Order Incomplete" ||
                  normalizedMsg === "Items not yet shipped to customer"
                ) {
                  setError("");
                  setIncompleteOpen(true);
                  return;
                }
                setError("");
                setErrorTitle("Error");
                setErrorMessage(errMsg || e?.message || "Failed to mark as paid");
                setErrorOpen(true);
              }
            }}
          >
            <LuCircleCheckBig className={styles.btnIcon} aria-hidden="true" />
            Mark as Paid
          </Button>
        </div>
      </div>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>

      {successOpen && (
        <OverlayModal
          onClose={() => setSuccessOpen(false)}
          closeStyle="plain"
          contentClassName={styles.modalNoFrame}
          showCloseButton={false}
        >
          <div className={styles.successModal}>
            <div className={styles.successIconWrap}>
              <LuCircleCheckBig className={styles.successIcon} aria-hidden="true" />
            </div>
            <div className={styles.successTitle}>Payment Complete!</div>
            <div className={styles.successSub}>Transaction processed successfully</div>

            <div className={styles.successMeta}>
              <div className={styles.successMetaRow}>
                <div className={styles.successMetaLabel}>Amount Paid</div>
                <div className={styles.successMetaValue}>{formatCurrency(totalDue)}</div>
              </div>
              <div className={styles.successMetaRow}>
                <div className={styles.successMetaLabel}>Payment Method</div>
                <div className={styles.successMetaValue}>COD</div>
              </div>
              <div className={styles.successMetaRow}>
                <div className={styles.successMetaLabel}>Receipt Number</div>
                <div className={styles.successMetaValue}>{receiptNo}</div>
              </div>
            </div>

            <Button
              variant="approve"
              className={styles.continueBtn}
              onClick={() => {
                setSuccessOpen(false);
                navigate("/frontshop/debtors/cod-accounts");
              }}
            >
              <LuArrowLeft className={styles.btnIcon} aria-hidden="true" />
              Continue
            </Button>
          </div>
        </OverlayModal>
      )}

      {incompleteOpen && (
        <OverlayModal
          onClose={() => setIncompleteOpen(false)}
          closeStyle="plain"
          contentClassName={styles.modalNoFrame}
          showCloseButton={false}
        >
          <div className={styles.successModal}>
            <div className={styles.errorIconWrap}>
              <LuCircleX className={styles.errorIcon} aria-hidden="true" />
            </div>
            <div className={styles.successTitle}>Order Incomplete</div>
            <div className={styles.successSub}>Items not yet shipped to customer</div>
            <div className={styles.successSub}>Unable to mark order as Paid</div>
            <Button variant="approve" className={styles.continueBtn} onClick={() => setIncompleteOpen(false)}>
              <LuArrowLeft className={styles.btnIcon} aria-hidden="true" />
              Continue
            </Button>
          </div>
        </OverlayModal>
      )}

      {errorOpen && (
        <OverlayModal
          onClose={() => setErrorOpen(false)}
          closeStyle="plain"
          contentClassName={styles.modalNoFrame}
          showCloseButton={false}
        >
          <div className={styles.successModal}>
            <div className={styles.errorIconWrap}>
              <LuCircleX className={styles.errorIcon} aria-hidden="true" />
            </div>
            <div className={styles.successTitle}>{errorTitle || "Error"}</div>
            <div className={styles.successSub}>{errorMessage || "Something went wrong"}</div>
            <Button variant="approve" className={styles.continueBtn} onClick={() => setErrorOpen(false)}>
              <LuArrowLeft className={styles.btnIcon} aria-hidden="true" />
              Continue
            </Button>
          </div>
        </OverlayModal>
      )}
    </div>
  );
}
